import { WidgetSize } from '@wix/wix-chatbot-common/browser';

import { TRootState } from '../../common/reducers';
import { PresentedState } from '../../common/reducers/types';
import { selectGenerateAnswerResult } from '../../common/selectors/answersGeneration';
import { selectCurrentPresentedState, selectSearchSessionId } from '../../common/selectors/helpCenter';
import { selectSolutions } from '../../common/selectors/solutions';
import { renderModel } from '../../util/renderModel';
import { biLogger } from '../biLogger';
import { selectBiData } from '../selectors';
import {
  createArticleSolutionInfo,
  createBreadcrumbsSolutionInfo,
  createCategoriesSolutionInfo,
  createChatWithUsSolutionInfo,
  createContactUsSolutionInfo,
  createFeatureButtonSolutionInfo,
  createHelpCenterSolutionInfo,
  createHirePartnerSolutionInfo,
  createKeraAssistantSolutionInfo,
  createResourcesSolutionInfo,
  createSearchResultsSolutionInfo,
  createShareFeedbackSolutionInfo,
  createSolutionListSolutionInfo,
  createStatusPageSolutionInfo,
  createSuccessManagerSolutionInfo,
  createTermsOfUseAndPrivacyPolicySolutionInfo,
} from '../solutionInfo';
import {
  AnswerGenBasedOnArticleClick,
  AnswerGenNoHelpfulReasonClick,
  AnswerGenSummarizedAnsLinkClick,
  ArticleActionBiData,
  ArticleAnchorClickedBiData,
  ArticleBiData,
  ArticleExternalLinkActionBiData,
  ArticleImageClickBiData,
  ArticleSectionActionBiData,
  ArticleSolutionLinkActionBiData,
  ArticleTabActionBiData,
  ArticleVoteActionBiData,
  BiEventLogger,
  BreadcrumbsClickBiData,
  ButtonBiData,
  ButtonClicked,
  CategoriesActionBiData,
  CategoryBadgeClick,
  CurrentPageBiData,
  HelpResourceBiData,
  MessageNotificationClickedBiData,
  PositionBiData,
  SearchSuggestedQuestionBiData,
  SearchSuggestedQuestionInArticleClick,
  SearchTryDifferentBiData,
  SolutionInfo,
} from '../types';
import {
  convertArticleType,
  getRuntimeDataForBi,
  pageEnumToString,
  presentedStateToPage,
  solutionInfoPageMapper,
} from '../utils';

export const logBookASessionLinkAction: BiEventLogger<ButtonBiData> = (state, { url, text, position }) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createSuccessManagerSolutionInfo(text, position, url, true),
    buttonClicked: ButtonClicked.ContactPartnerSuccessTeam,
  });
};

export const logRequestAFeatureAction: BiEventLogger<ButtonBiData> = (state, { text, position, url }) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createFeatureButtonSolutionInfo(text, position, url),
    buttonClicked: ButtonClicked.RequestFeature,
  });
};

export const logReportABugAction: BiEventLogger<ButtonBiData> = (state, { text, position }) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createShareFeedbackSolutionInfo(text, position),
    buttonClicked: ButtonClicked.ReportBug,
  });
};

export const logBreadcrumbsClickAction: BiEventLogger<BreadcrumbsClickBiData> = (state, data) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createBreadcrumbsSolutionInfo(data),
    buttonClicked: ButtonClicked.Breadcrumbs,
  });
};

export const logArticleTabAction: BiEventLogger<ArticleTabActionBiData> = (
  state,
  { tabName, tabIndex, articleId },
) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createArticleSolutionInfo({
      articleId,
      linkText: tabName,
      position: tabIndex,
    }),
    buttonClicked: ButtonClicked.ArticleTabSelection,
    clickedValue: tabName,
    directLinkData: {
      text: tabName,
    },
  });
};

export const logMessageNotificationClickedAction: BiEventLogger<MessageNotificationClickedBiData> = (
  state,
  { clickedValue, currentPage },
) => {
  void widgetContentInteraction({
    state,
    buttonClicked: ButtonClicked.Notifications,
    clickedValue,
    solutionInfo: solutionInfoPageMapper[currentPage](state),
  });
};

export const logWidgetCollapseClickedAction: BiEventLogger<CurrentPageBiData> = (state, { currentPage }) => {
  void widgetContentInteraction({
    state,
    buttonClicked: ButtonClicked.WidgetCollapse,
    solutionInfo: solutionInfoPageMapper[currentPage](state),
  });
};

export const logToggleWidgetSizeClickedAction: BiEventLogger<CurrentPageBiData> = (
  state,
  { currentPage },
) => {
  void widgetContentInteraction({
    state,
    buttonClicked:
      state.widget.size === WidgetSize.Default ? ButtonClicked.WidgetMaximize : ButtonClicked.WidgetMinimize,
    solutionInfo: solutionInfoPageMapper[currentPage](state),
  });
};

export const logBackButtonAction: BiEventLogger<CurrentPageBiData> = (state, { currentPage }) => {
  void widgetContentInteraction({
    state,
    solutionInfo: solutionInfoPageMapper[currentPage](state),
    buttonClicked: ButtonClicked.Back,
  });
};

export const logCopyEmailButtonClickAction: BiEventLogger<ButtonBiData> = (state, { text, position }) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createSuccessManagerSolutionInfo(text, position),
    buttonClicked: ButtonClicked.ContactPartnerSuccessTeam,
  });
};

export const logOpenEmailButtonClickAction: BiEventLogger<ButtonBiData> = (state, { text, position }) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createSuccessManagerSolutionInfo(text, position),
    buttonClicked: ButtonClicked.ContactPartnerSuccessTeam,
  });
};

export const logStatusPageAction: BiEventLogger<Required<ButtonBiData>> = (
  state,
  { url, text, position },
) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createStatusPageSolutionInfo(url, text, position),
    buttonClicked: ButtonClicked.StatusPage,
  });
};

export const logTermsOfUseClickAction: BiEventLogger<Required<ButtonBiData>> = (
  state,
  { url, text, position },
) => {
  // Terms/Privacy links could appear in footers on multiple pages

  void widgetContentInteraction({
    state,
    solutionInfo: createTermsOfUseAndPrivacyPolicySolutionInfo(url, text, position),
    buttonClicked: ButtonClicked.TermsOfUse,
  });
};

export const logPrivacyPolicyClickAction: BiEventLogger<Required<ButtonBiData>> = (
  state,
  { url, text, position },
) => {
  // Terms/Privacy links could appear in footers on multiple pages

  void widgetContentInteraction({
    state,
    solutionInfo: createTermsOfUseAndPrivacyPolicySolutionInfo(url, text, position),
    buttonClicked: ButtonClicked.PrivacyPolicy,
  });
};

export const logContactUsButtonAction: BiEventLogger<ButtonBiData> = (state, { text, position }) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createContactUsSolutionInfo(text, position),
    buttonClicked: ButtonClicked.ContactUs,
  });
};

export const logHirePartnerButtonAction: BiEventLogger<Required<ButtonBiData>> = (state, button) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createHirePartnerSolutionInfo(button),
    buttonClicked: ButtonClicked.HirePartner,
  });
};

export const logHelpResourcesButtonAction: BiEventLogger<HelpResourceBiData> = (state, resources) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createResourcesSolutionInfo(resources),
    buttonClicked: resources.buttonClickedBiName,
    directLinkData: resources.url
      ? {
          url: resources.url,
          text: resources.text,
        }
      : undefined,
  });
};

export const logChatWithUsAction: BiEventLogger<PositionBiData> = (state, { position }) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createChatWithUsSolutionInfo(position),
    buttonClicked: ButtonClicked.ChatWithUs,
  });
};

export const logHelpCenterButtonAction: BiEventLogger<PositionBiData> = (state, { position }) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createHelpCenterSolutionInfo(position),
    buttonClicked: ButtonClicked.HelpCenter,
  });
};

export const logKeraAssistantAction: BiEventLogger<PositionBiData> = (state, { position }) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createKeraAssistantSolutionInfo(position),
    buttonClicked: ButtonClicked.KeraVoiceAssistantCta,
  });
};

export const logCategoriesAction: BiEventLogger<CategoriesActionBiData> = (state, { category, position }) => {
  const presentedState = selectCurrentPresentedState(state);
  const isSubCategory = presentedState === PresentedState.SubCategoriesList;

  void widgetContentInteraction({
    state,
    buttonClicked: isSubCategory ? ButtonClicked.SubCategorySelection : ButtonClicked.CategorySelection,
    solutionInfo: createCategoriesSolutionInfo([category], isSubCategory, position),
  });
};

export const logCloseFullPageSidebarAction: BiEventLogger<ArticleBiData> = (state, { id, title }) => {
  void widgetContentInteraction({
    state,
    buttonClicked: ButtonClicked.CloseKbWidget,
    solutionInfo: createArticleSolutionInfo({
      articleId: id,
      linkText: title,
    }),
  });
};

export const logArticleExternalLinkAction: BiEventLogger<ArticleExternalLinkActionBiData> = (
  state,
  { id, title, url, isCta },
) => {
  void widgetContentInteraction({
    state,
    buttonClicked: isCta ? ButtonClicked.ArticleCta : ButtonClicked.ArticleExternalLink,
    solutionInfo: createArticleSolutionInfo({
      articleId: id,
      linkText: title,
      linkUrl: url,
    }),
    clickedValue: title,
    articleId: id,
    directLinkData: {
      url: url || '',
      text: title || '',
    },
  });
};

export const logArticleOpenInNewTabAction: BiEventLogger<ArticleExternalLinkActionBiData> = (
  state,
  { id, title, url },
) => {
  void widgetContentInteraction({
    state,
    buttonClicked: ButtonClicked.OpenInNewTab,
    solutionInfo: createArticleSolutionInfo({
      articleId: id,
      linkText: title,
      linkUrl: url,
    }),
    clickedValue: title,
    articleId: id,
    directLinkData: {
      url: url || '',
      text: title || '',
    },
  });
};

export const logArticleSectionAction: BiEventLogger<ArticleSectionActionBiData> = (
  state,
  { expand, linkText, articleId },
) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createArticleSolutionInfo({ articleId, linkText }),
    buttonClicked: expand ? ButtonClicked.ExpandArticleSection : ButtonClicked.CollapseArticleSection,
  });
};

export const logWidgetMaximizeMinimizeAction: BiEventLogger<ArticleSectionActionBiData> = (
  state,
  { expand, linkText, articleId },
) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createArticleSolutionInfo({ articleId, linkText }),
    buttonClicked: expand ? ButtonClicked.WidgetMaximize : ButtonClicked.WidgetMinimize,
  });
};

export const logOpenArticleInHCAction: BiEventLogger<ArticleBiData> = async (state, { id, url, title }) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createArticleSolutionInfo({ articleId: id }),
    buttonClicked: ButtonClicked.OpenArticleInHC,
    articleId: id,
    clickedValue: title,
    directLinkData: url
      ? {
          url,
          text: title || '',
        }
      : undefined,
  });
};

export const logRecommendedArticleAction: BiEventLogger<ArticleActionBiData> = (state, { id, position }) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createArticleSolutionInfo({ articleId: id, position }),
    buttonClicked: ButtonClicked.RecommendedArticle,
  });
};

export const logArticleSolutionLinkAction: BiEventLogger<ArticleSolutionLinkActionBiData> = (
  state,
  { article, isExternalLink, linkText, linkSectionText, linkUrl, isCta },
) => {
  const getButtonType = () => {
    if (isCta) {
      return ButtonClicked.ArticleCta;
    }
    return ButtonClicked.ArticleLink;
  };

  void widgetContentInteraction({
    state,
    articleId: article.id,
    lang: article.locale,
    buttonClicked: getButtonType(),
    solutionInfo: createArticleSolutionInfo({
      articleId: article.id,
      linkText,
      linkSectionText,
    }),
    clickedValue: linkText,
    directLinkData: {
      url: linkUrl || '',
      text: linkText,
    },
  });
};

export const logCategorySolutionListAction: BiEventLogger<ArticleActionBiData> = (
  state,
  { id, position, categoryId },
) => {
  void widgetContentInteraction({
    state,
    categoryId,
    buttonClicked: ButtonClicked.SolutionClicked,
    solutionInfo: createSolutionListSolutionInfo([{ id }], position),
  });
};

export const logSearchResultsAction: BiEventLogger<ArticleActionBiData> = (
  state,
  { articleType, id, distance, position },
) => {
  void widgetContentInteraction({
    state,
    articleId: id,
    solutionInfo: createSearchResultsSolutionInfo(
      [{ articleType: convertArticleType(articleType), id, distance }],
      undefined,
      position,
    ),
    buttonClicked: ButtonClicked.SolutionClicked,
  });
};

export const logSearchSuggestedQuestionInArticleAction: BiEventLogger<
  SearchSuggestedQuestionInArticleClick
> = (state, { id, searchedText }) => {
  const searchSessionId = selectSearchSessionId(state);
  void widgetContentInteraction({
    state,
    articleId: id,
    searchedText,
    buttonClicked: ButtonClicked.SearchSuggestedQuestionClicked,
    solutionInfo: undefined,
    searchSessionId,
  });
};

export const logSearchResultsActionV2: BiEventLogger<ArticleActionBiData> = (
  state,
  { articleType, id, distance, position },
) => {
  void widgetContentInteraction({
    state,
    articleId: id,
    distance: distance ? distance : undefined,
    solutionInfo: createSearchResultsSolutionInfo(
      [{ articleType: convertArticleType(articleType), id, distance }],
      undefined,
      position,
    ),
    buttonClicked: ButtonClicked.SolutionClicked,
  });
};

export const logClearSearchAction: BiEventLogger = (state) => {
  const solutions = selectSolutions(state);

  void widgetContentInteraction({
    state,
    solutionInfo: createSearchResultsSolutionInfo(solutions ?? []),
    buttonClicked: ButtonClicked.ClearSearchBar,
  });
};

export const logSearchBarAction: BiEventLogger<CurrentPageBiData> = (state, { currentPage }) => {
  void widgetContentInteraction({
    state,
    buttonClicked: ButtonClicked.SearchBarTextTyping,
    solutionInfo: solutionInfoPageMapper[currentPage](state),
  });
};

export const logSearchButtonClickedAction: BiEventLogger<CurrentPageBiData> = (state, { currentPage }) => {
  void widgetContentInteraction({
    state,
    buttonClicked: ButtonClicked.SearchButtonClicked,
    solutionInfo: solutionInfoPageMapper[currentPage](state),
  });
};

export const logArticleVoteButtonClick: BiEventLogger<ArticleVoteActionBiData> = (
  state,
  { articleId, buttonClicked },
) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createArticleSolutionInfo({
      articleId,
    }),
    buttonClicked,
  });
};
export const logArticleAnchorClick: BiEventLogger<ArticleAnchorClickedBiData> = (
  state,
  { id, clickedValue },
) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createArticleSolutionInfo({ articleId: id }),
    buttonClicked: ButtonClicked.ArticleInternalAnchor,
    clickedValue,
  });
};

export const logArticleImageClick: BiEventLogger<ArticleImageClickBiData> = (
  state,
  { articleId, imageSrc },
) => {
  void widgetContentInteraction({
    state,
    solutionInfo: createArticleSolutionInfo({ articleId }),
    buttonClicked: ButtonClicked.ArticleImage,
    clickedValue: imageSrc,
  });
};

export const logArticleVideoPlay: BiEventLogger<{ articleId?: string; videoUrl: string }> = (
  state,
  { articleId, videoUrl },
) => {
  void widgetContentInteraction({
    state,
    solutionInfo: articleId ? createArticleSolutionInfo({ articleId }) : undefined,
    buttonClicked: ButtonClicked.ArticleVideoPlay,
    clickedValue: videoUrl,
    articleId,
    directLinkData: {
      url: videoUrl,
    },
  });
};

export const logCategoryBadgeClick: BiEventLogger<CategoryBadgeClick> = (
  state: TRootState,
  { articleId, distance, position, categoryId, categoryName },
): void => {
  void widgetContentInteraction({
    state,
    categoryId,
    buttonClicked: ButtonClicked.SearchResultCategoryButton,
    solutionInfo: createSearchResultsSolutionInfo([{ id: articleId, distance }], categoryName, position),
  });
};

export const logSearchSuggestedQuestionClick: BiEventLogger<SearchSuggestedQuestionBiData> = (
  state,
  { searchedText },
) => {
  void widgetContentInteraction({
    state,
    searchedText,
    buttonClicked: ButtonClicked.SearchSuggestedQuestion,
    solutionInfo: undefined,
  });
};

export const logSearchTryDifferentClick: BiEventLogger<SearchTryDifferentBiData> = (
  state,
  { searchedText },
) => {
  const searchSessionId =
    state.helpCenter.currentPresentedState === PresentedState.SearchResults
      ? selectSearchSessionId(state)
      : undefined;

  void widgetContentInteraction({
    state,
    searchedText,
    searchSessionId,
    buttonClicked: ButtonClicked.SearchTryDifferent,
    solutionInfo: undefined,
  });
};

export const logAnswerGenSummarizedAnsLinkClick: BiEventLogger<AnswerGenSummarizedAnsLinkClick> = (
  state: TRootState,
  { url },
): void => {
  void widgetContentInteraction({
    state,
    buttonClicked: ButtonClicked.AnswerGenSummarizedAnsLink,
    solutionInfo: { url },
  });
};

export const logAnswerGenBasedOnArticleClick: BiEventLogger<AnswerGenBasedOnArticleClick> = (
  state: TRootState,
  { url, articleId },
): void => {
  void widgetContentInteraction({
    state,
    articleId,
    buttonClicked: ButtonClicked.AnswerGenSummarizedAnsBasedArticle,
    solutionInfo: { url },
  });
};

export const logAnswerGenVoteNotHelpfulClick: BiEventLogger = (state) => {
  void widgetContentInteraction({
    state,
    buttonClicked: ButtonClicked.AnswerGenVoteNotHelpful,
    solutionInfo: undefined,
  });
};

export const logAnswerGenVoteHelpfulClick: BiEventLogger = (state) => {
  void widgetContentInteraction({
    state,
    buttonClicked: ButtonClicked.AnswerGenVoteHelpful,
    solutionInfo: undefined,
  });
};

export const logAnswerGenNotHelpfulReasonClick: BiEventLogger<AnswerGenNoHelpfulReasonClick> = (
  state: TRootState,
  { clickedValue },
): void => {
  void widgetContentInteraction({
    state,
    buttonClicked: ButtonClicked.AnswerGenNoHelpfulReason,
    solutionInfo: undefined,
    clickedValue,
  });
};

export const logContactSupportClick: BiEventLogger = (state) => {
  void widgetContentInteraction({
    state,
    buttonClicked: ButtonClicked.SupportRequestHyperlink,
    solutionInfo: undefined,
  });
};

export const logKeraVoiceAssistamtModalApproveClick: BiEventLogger = (state) => {
  void widgetContentInteraction({
    state,
    buttonClicked: ButtonClicked.KeraVoiceAssistantAnnouncementApprove,
    solutionInfo: undefined,
  });
};

async function widgetContentInteraction({
  state,
  buttonClicked,
  solutionInfo,
  clickedValue,
  articleId,
  categoryId,
  lang,
  searchedText,
  searchSessionId,
  summarizedAnswerBasedOn,
  summarizedAnswerText,
  summarizedAnswerType,
  summarizedAnswerWordsCount,
  distance,
  directLinkData,
}: {
  state: TRootState;
  buttonClicked: ButtonClicked;
  solutionInfo: SolutionInfo | SolutionInfo[] | undefined | { url: string };
  clickedValue?: string;
  articleId?: string;
  categoryId?: string;
  lang?: string;
  searchedText?: string;
  searchSessionId?: string;
  summarizedAnswerBasedOn?: string;
  summarizedAnswerText?: string;
  summarizedAnswerType?: string;
  summarizedAnswerWordsCount?: number;
  distance?: number;
  directLinkData?: { url?: string; text?: string };
}): Promise<void> {
  const biData = await selectBiData(state);
  const runtimeData = await getRuntimeDataForBi(state);
  const currentPresentedState = selectCurrentPresentedState(state);
  const currentPage = presentedStateToPage(currentPresentedState);
  const widgetPageType = pageEnumToString(currentPage);

  const { contextBi, sessionId } = runtimeData;
  const contextPlatform = contextBi?.context_platform;

  const BUTTON_CLICKED_NOT_TO_FIRE = [
    ButtonClicked.AnswerGenNoHelpfulReason,
    ButtonClicked.AnswerGenVoteHelpful,
    ButtonClicked.AnswerGenVoteNotHelpful,
    ButtonClicked.AnswerGenSummarizedAnsBasedArticle,
    ButtonClicked.AnswerGenSummarizedAnsLink,
    ButtonClicked.SolutionClicked,
    ButtonClicked.SearchResultCategoryButton,
    ButtonClicked.WidgetCollapse,
  ];

  if (!BUTTON_CLICKED_NOT_TO_FIRE.includes(buttonClicked)) {
    if (contextPlatform === 'contact') {
      // 1521 - Full Contact Page Article Engagement
      const accountId = renderModel.getAccountId();
      const articleLocale = renderModel.getLocale();

      biLogger.fullContactPageArticleEngagement(state, {
        chatbot_conversation_id: biData.conversationId,
        self_service_session_id: sessionId,
        target_account_id: accountId,
        article_id: (articleId ?? biData.articleId)?.toString(),
        article_language: articleLocale,
        clicked_url: directLinkData?.url,
        clicked_text: directLinkData?.text || clickedValue,
        clicked_item_type: buttonClicked,
      });
    } else {
      // 1506
      biLogger.helpWidgetItemClickNew(state, {
        presented_content: JSON.stringify(solutionInfo ?? null),
        clicked_url: directLinkData ? directLinkData?.url : undefined,
        clicked_text: directLinkData ? directLinkData?.text : clickedValue,
        clicked_item_type: buttonClicked,
        searched_text: searchedText ?? biData.searchedText,
        search_session_id: searchSessionId,
        widget_page_type: widgetPageType,
      });
    }
  }
}

async function logAnswerGen1510Event(
  state: TRootState,
  clickedItemType: string,
  params: {
    answer_type?: string;
    clicked_url?: string;
    clicked_item_id?: string;
    clicked_text?: string;
  } = {},
): Promise<void> {
  const biData = await selectBiData(state);
  const searchSessionId = biData.searchSessionId || selectSearchSessionId(state);
  const currentPresentedState = selectCurrentPresentedState(state);
  const currentPage = presentedStateToPage(currentPresentedState);
  const widgetPageType = pageEnumToString(currentPage);
  const solutionInfo = solutionInfoPageMapper[currentPage](state);
  const solutions = selectSolutions(state);
  const answerGenResult = selectGenerateAnswerResult(state);

  const hasGeneratedAnswer = answerGenResult && (answerGenResult.answer || answerGenResult.prohibited);
  let answerType = solutions && solutions.length ? 'list' : 'empty';

  if (hasGeneratedAnswer) {
    answerType = 'generated answer';
  }

  const searchMethod = 'Knowledge Agent';

  await biLogger.helpWidgetClickOnAnswerOrResultNew(state, {
    clicked_item_type: clickedItemType,
    clicked_url: params.clicked_url,
    clicked_item_id: params.clicked_item_id,
    clicked_text: params.clicked_text,
    answer_type: params.answer_type || answerType,
    searched_text: biData.searchedText,
    widget_page_type: widgetPageType,
    presented_content: JSON.stringify(solutionInfo ?? null),
    search_session_id: searchSessionId,
    search_method: searchMethod,
  });
}

export const logAnswerGenSummarizedAnsHyperlink1510: BiEventLogger<AnswerGenSummarizedAnsLinkClick> = async (
  state: TRootState,
  { url },
): Promise<void> => {
  await logAnswerGen1510Event(state, 'summarized_answer_hyperlink', { clicked_url: url });
};

export const logAnswerGenBasedOnArticle1510: BiEventLogger<AnswerGenBasedOnArticleClick> = async (
  state: TRootState,
  { url, articleId },
): Promise<void> => {
  await logAnswerGen1510Event(state, 'summarized_answer_based_article', {
    clicked_url: url,
    clicked_item_id: articleId,
  });
};

export const logAnswerGenVoteHelpful1510: BiEventLogger = async (state: TRootState): Promise<void> => {
  await logAnswerGen1510Event(state, 'summarized_answer_helpful');
};

export const logAnswerGenVoteNotHelpful1510: BiEventLogger = async (state: TRootState): Promise<void> => {
  await logAnswerGen1510Event(state, 'summarized_answer_not_helpful');
};

export const logAnswerGenNotHelpfulReason1510: BiEventLogger<{
  clickedValue: string;
  answerType?: string;
}> = async (state: TRootState, { clickedValue, answerType }): Promise<void> => {
  await logAnswerGen1510Event(state, 'summarized_answer_not_helpful_reason', {
    clicked_text: clickedValue,
    answer_type: answerType,
  });
};

export const logContactSupportClickNew: BiEventLogger<{ url: string }> = async (
  state: TRootState,
  { url },
): Promise<void> => {
  await logAnswerGen1510Event(state, 'support_request_hyperlink', {
    answer_type: 'support request',
    clicked_url: url,
  });
};
